import React, { FunctionComponent, useState } from "react";

import {
  ButtonsGroupChoiceField,
  DefaultButton,
  Dialog,
  SubmissionForm,
  TextInputField
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { BaseUserStatuses } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { useCreateUser } from "@libs/api/gateways/plt/plt-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

interface NewUserDialogButtonProps {
  tenantId: string;
  onDismiss: () => void;
}

interface NewUserFormValues {
  firstName: string;
  lastName: string;
  status: BaseUserStatuses;
}

const newUserDialogValidator = new Validator<NewUserFormValues>(validator => ({
  firstName: validator
    .string()
    .required()
    .trim(),
  lastName: validator
    .string()
    .required()
    .trim()
}));

const NewUserDialogButtonBase: FunctionComponent<NewUserDialogButtonProps> = ({
  onDismiss,
  tenantId
}) => {
  const { mutateAsync: createUser } = useCreateUser();
  const onSubmit = async (values: NewUserFormValues) => {
    try {
      await createUser({
        tenantId,
        firstName: values.firstName,
        lastName: values.lastName,
        status: values.status
      });
      onDismiss();
    } catch (e) {
      throw e;
    }
  };

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title: "New user",
        showCloseButton: true,
        onDismiss
      }}
    >
      <SubmissionForm<NewUserFormValues>
        initialValues={{
          firstName: "",
          lastName: "",
          status: BaseUserStatuses.active
        }}
        onSubmit={onSubmit}
        cancelButtonProps={{ title: "Close", onClick: onDismiss }}
        validate={newUserDialogValidator.validate}
      >
        {() => (
          <>
            <TextInputField label="First name" name="firstName" required />
            <TextInputField label="Last name" name="lastName" required />
            <ButtonsGroupChoiceField
              options={Object.values(BaseUserStatuses).map(s => ({
                key: s,
                text: s
              }))}
              name="status"
              label="Status"
            />
          </>
        )}
      </SubmissionForm>
    </Dialog>
  );
};

export const NewUserDialogButton: FunctionComponent<Omit<
  NewUserDialogButtonProps,
  "onDismiss"
> & {
  disabled?: boolean;
  disabledTitle?: string;
}> = ({ tenantId, disabled, disabledTitle }) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltCatalogUserWrite
  );

  const isDisabled = !hasWritePermission || disabled;
  let title: string | undefined;
  if (!hasWritePermission) {
    title = NO_ACTION_PERMISSION;
  } else if (disabled) {
    title = disabledTitle;
  }

  return (
    <>
      <DefaultButton
        onClick={() => setShowDialog(true)}
        disabled={isDisabled}
        title={title}
      >
        Create user
      </DefaultButton>
      {showDialog && (
        <NewUserDialogButtonBase
          onDismiss={() => setShowDialog(false)}
          tenantId={tenantId}
        />
      )}
    </>
  );
};
